import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  People,
  Star
} from '@mui/icons-material';
import { ResponsiveRadar } from '@nivo/radar';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { advancedReportsAPI } from '../../services/api';

interface TeamMetrics {
  team_id: number;
  team_name: string;
  agent_count: number;
  total_conversations: number;
  conversations_per_agent: number;
  avg_resolution_time: number;
  resolution_rate: number;
  avg_csat: number;
  efficiency_score: number;
  rank: number;
  collaboration_index: number;
  escalation_rate: number;
}

interface TeamComparisonData {
  teams: TeamMetrics[];
  best_performing_team: TeamMetrics;
  benchmarks: {
    avg_efficiency_score: number;
    avg_resolution_time: number;
    avg_csat: number;
  };
  radar_data: Array<{
    metric: string;
    [key: string]: string | number;
  }>;
}

interface TeamPerformanceAnalyticsProps {
  startDate: string;
  endDate: string;
}

const TeamPerformanceAnalytics: React.FC<TeamPerformanceAnalyticsProps> = ({
  startDate,
  endDate
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeamComparisonData | null>(null);

  useEffect(() => {
    fetchTeamData();
  }, [startDate, endDate]);

  const fetchTeamData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advancedReportsAPI.getTeamAnalytics({
        start_date: startDate,
        end_date: endDate
      });

      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch team analytics:', err);
      setError(err.message || 'Failed to load team performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data || !data.teams.length) {
    return <Alert severity="info">No team performance data available for the selected period.</Alert>;
  }

  // Validate best_performing_team exists
  if (!data.best_performing_team) {
    return <Alert severity="warning">Team performance data is incomplete. Please ensure teams have sufficient activity in the selected period.</Alert>;
  }

  const getRankColor = (rank: number) => {
    if (rank === 1) return '#FFD700'; // Gold
    if (rank === 2) return '#C0C0C0'; // Silver
    if (rank === 3) return '#CD7F32'; // Bronze
    return '#e0e0e0';
  };

  const getPerformanceTier = (score: number) => {
    if (score >= 90) return { label: 'Excellent', color: 'success' };
    if (score >= 80) return { label: 'Good', color: 'primary' };
    if (score >= 70) return { label: 'Average', color: 'warning' };
    return { label: 'Needs Improvement', color: 'error' };
  };

  // Bar chart for efficiency scores
  const barChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      offsetX: -6,
      style: {
        fontSize: '12px',
        colors: ['#fff']
      }
    },
    xaxis: {
      categories: data.teams.map(t => t.team_name),
      max: 100,
      labels: {
        formatter: (val) => `${val}%`
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: ['#1f93ff'],
    grid: {
      borderColor: '#e7e7e7'
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(1)}%`
      }
    }
  };

  return (
    <Box>
      {/* Top Performer Card */}
      <Card sx={{ mb: 3, bgcolor: '#fff8e1', border: '2px solid #FFD700' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: '#FFD700', width: 56, height: 56 }}>
              <EmojiEvents fontSize="large" />
            </Avatar>
            <Box flex={1}>
              <Typography variant="overline" color="textSecondary">
                Best Performing Team
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {data.best_performing_team.team_name}
              </Typography>
              <Box display="flex" gap={2} mt={1}>
                <Chip
                  label={`Efficiency Score: ${data.best_performing_team.efficiency_score}%`}
                  color="primary"
                  icon={<Star />}
                />
                <Chip
                  label={`${data.best_performing_team.agent_count} Agents`}
                  icon={<People />}
                />
                <Chip
                  label={`${data.best_performing_team.total_conversations.toLocaleString()} Conversations`}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Benchmark Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Average Efficiency Score
              </Typography>
              <Typography variant="h4" color="primary">
                {(data.benchmarks.avg_efficiency_score || 0).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Across all teams
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Avg Resolution Time
              </Typography>
              <Typography variant="h4" color="primary">
                {Math.floor(data.benchmarks.avg_resolution_time || 0)}m
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Team average
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Average CSAT Score
              </Typography>
              <Typography variant="h4" color="primary">
                {(data.benchmarks.avg_csat || 0).toFixed(1)} / 5.0
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Customer satisfaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Team Radar Chart */}
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Performance Comparison (Radar)
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Multi-dimensional performance across key metrics
            </Typography>
            <Box height={450}>
              <ResponsiveRadar
                data={data.radar_data}
                keys={data.teams.map(t => t.team_name)}
                indexBy="metric"
                valueFormat=">-.0f"
                margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
                borderColor={{ from: 'color' }}
                gridLabelOffset={36}
                dotSize={10}
                dotColor={{ theme: 'background' }}
                dotBorderWidth={2}
                colors={{ scheme: 'nivo' }}
                blendMode="multiply"
                motionConfig="wobbly"
                legends={[
                  {
                    anchor: 'top-left',
                    direction: 'column',
                    translateX: -50,
                    translateY: -40,
                    itemWidth: 80,
                    itemHeight: 20,
                    itemTextColor: '#999',
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemTextColor: '#000'
                        }
                      }
                    ]
                  }
                ]}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Efficiency Score Bar Chart */}
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Efficiency Scores
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Weighted score: Resolution Rate (40%), Response Time (30%), CSAT (30%)
            </Typography>
            <Chart
              options={barChartOptions}
              series={[{
                name: 'Efficiency Score',
                data: data.teams.map(t => t.efficiency_score)
              }]}
              type="bar"
              height={350}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Team Comparison Table */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Team Comparison
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell align="center">Agents</TableCell>
                <TableCell align="right">Conversations</TableCell>
                <TableCell align="right">Conv/Agent</TableCell>
                <TableCell align="right">Avg Resolution</TableCell>
                <TableCell align="right">Resolution Rate</TableCell>
                <TableCell align="right">CSAT</TableCell>
                <TableCell align="right">Efficiency Score</TableCell>
                <TableCell align="center">Performance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.teams.map((team) => {
                const tier = getPerformanceTier(team.efficiency_score);
                return (
                  <TableRow key={team.team_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {team.rank <= 3 && (
                          <EmojiEvents sx={{ color: getRankColor(team.rank) }} />
                        )}
                        <Typography variant="body2" fontWeight="bold">
                          #{team.rank}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {team.team_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={team.agent_count}
                        size="small"
                        icon={<People />}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {team.total_conversations.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {team.conversations_per_agent}
                    </TableCell>
                    <TableCell align="right">
                      {team.avg_resolution_time}m
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                        <Typography variant="body2">
                          {team.resolution_rate.toFixed(1)}%
                        </Typography>
                        {team.resolution_rate >= data.benchmarks.avg_efficiency_score ? (
                          <TrendingUp fontSize="small" color="success" />
                        ) : (
                          <TrendingDown fontSize="small" color="error" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                        {team.avg_csat.toFixed(1)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {team.efficiency_score.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={team.efficiency_score}
                          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                          color={tier.color as any}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tier.label}
                        size="small"
                        color={tier.color as any}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Additional Metrics */}
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Workload Distribution
            </Typography>
            <Box>
              {data.teams.map((team) => (
                <Box key={team.team_id} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{team.team_name}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {team.conversations_per_agent} conv/agent
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(team.conversations_per_agent / 150) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Collaboration & Escalation
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Team</TableCell>
                    <TableCell align="right">Collaboration Index</TableCell>
                    <TableCell align="right">Escalation Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.teams.map((team) => (
                    <TableRow key={team.team_id}>
                      <TableCell>{team.team_name}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${team.collaboration_index}%`}
                          size="small"
                          color={team.collaboration_index >= 75 ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${team.escalation_rate}%`}
                          size="small"
                          color={team.escalation_rate <= 15 ? 'success' : 'warning'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Best Practices & Insights */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f1f8ff', borderLeft: '4px solid #1f93ff' }}>
        <Typography variant="h6" gutterBottom>
          📊 Performance Insights & Best Practices
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              ✅ Top Performing Teams
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="caption">
                  <strong>{data.teams[0].team_name}</strong>: Excellent efficiency score ({data.teams[0].efficiency_score}%) - Share their practices organization-wide
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  <strong>{data.teams.find(t => t.avg_csat === Math.max(...data.teams.map(t => t.avg_csat)))?.team_name}</strong>: Highest customer satisfaction - Study their customer communication approach
                </Typography>
              </li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              💡 Improvement Opportunities
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="caption">
                  Teams with high escalation rates need additional training or resources
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  Balance workload across teams - some agents are handling 60% more conversations
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  Improve collaboration index in teams below 70% through cross-training
                </Typography>
              </li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TeamPerformanceAnalytics;
